// Subscription product editing helpers (mirror backend SubProductIn).
export const SUB_TIERS = [
  { key: "crushr", title: "Crushr" },
  { key: "winkr", title: "Winkr" },
  { key: "duo", title: "Duo" },
];
export const PERIODS = [
  { key: "week", label: "1 week", weeks: 1 },
  { key: "month", label: "1 month", weeks: 52 / 12 },
  { key: "3mo", label: "3 months", weeks: 13 },
  { key: "6mo", label: "6 months", weeks: 26 },
];
const PID_RE = /^[A-Za-z0-9_.]+$/;

export const perWeek = (price, period) => {
  const w = PERIODS.find((p) => p.key === period)?.weeks;
  return w && +price > 0 ? (+price / w).toFixed(2) : null;
};

// One row per (tier, period); rows the API doesn't have yet start empty and are only sent once filled in.
export function toSubRows(products) {
  const by = Object.fromEntries((products || []).map((p) => [`${p.tier}:${p.period}`, p]));
  return SUB_TIERS.flatMap((t) => PERIODS.map((pe) => {
    const p = by[`${t.key}:${pe.key}`];
    return { k: `${t.key}:${pe.key}`, tier: t.key, period: pe.key, exists: !!p, price: p ? Number(p.price_usd).toFixed(2) : "",
      store_product_id: p?.store_product_id || "", active: p ? p.active !== false : true, legacy: p?.legacy_product_ids || [],
      save_pct: p?.save_pct ? String(p.save_pct) : "", save_orig: p?.save_pct || null };
  }));
}

const filled = (r) => r.exists || r.price.trim() || r.store_product_id.trim();

export function validateSubs(rows) {
  const errors = {};
  const ids = {};
  rows.filter(filled).forEach((r) => {
    const e = {};
    if (!/^\d+(\.\d{1,2})?$/.test(r.price) || +r.price <= 0 || +r.price > 999.99) e.price = "0.01–999.99";
    const id = r.store_product_id.trim();
    if (id.length < 3 || id.length > 120 || !PID_RE.test(id)) e.store_product_id = "3–120 letters, numbers, _ or .";
    else if (ids[id]) { e.store_product_id = "Same ID as another product"; errors[ids[id]] = { ...errors[ids[id]], store_product_id: "Same ID as another product" }; }
    ids[id] = r.k;
    if (Object.keys(e).length) errors[r.k] = { ...errors[r.k], ...e };
  });
  return errors;
}

export const subsPayload = (rows) => rows.filter(filled).map((r) => ({
  tier: r.tier, period: r.period, price_usd: Math.round(+r.price * 100) / 100, store_product_id: r.store_product_id.trim(), active: r.active,
  // Empty sends 0 (clears) only when a badge was saved; otherwise it is left out. The server checks the range.
  ...(r.save_pct !== "" ? { save_pct: +r.save_pct } : r.save_orig ? { save_pct: 0 } : {}),
}));

const label = (r) => `${SUB_TIERS.find((t) => t.key === r.tier).title} · ${PERIODS.find((p) => p.key === r.period).label}`;
const money = (v) => `$${Number(v).toFixed(2)}`;

export function diffSubs(products, rows) {
  const by = Object.fromEntries((products || []).map((p) => [`${p.tier}:${p.period}`, p]));
  return rows.filter(filled).flatMap((r) => {
    const o = by[r.k];
    if (!o) return [`Add ${label(r)} at ${r.price ? money(r.price) : "?"} (${r.store_product_id || "no ID"})`];
    const parts = [];
    if (Math.round(+r.price * 100) !== Math.round(o.price_usd * 100)) parts.push(`price ${money(o.price_usd)} → ${r.price ? money(r.price) : "?"}`);
    if (r.store_product_id.trim() !== o.store_product_id) parts.push(`store ID ${o.store_product_id} → ${r.store_product_id.trim()} (old ID kept as legacy)`);
    if (r.active !== (o.active !== false)) parts.push(r.active ? "turned on" : "turned off");
    if ((+r.save_pct || null) !== (o.save_pct || null)) parts.push(`save ${o.save_pct ? `${o.save_pct}%` : "none"} → ${+r.save_pct ? `${+r.save_pct}%` : "none"}`);
    return parts.length ? [`${label(r)}: ${parts.join(", ")}`] : [];
  });
}

// FastAPI 422 detail (loc: ["body","subscription_products", i, field]) → row keys of the filled rows we sent.
export function serverSubErrors(err, rows) {
  if (!Array.isArray(err?.data)) return {};
  const sent = rows.filter(filled);
  return err.data.reduce((acc, d) => {
    const [, list, i, f] = d.loc || [];
    if (list !== "subscription_products" || !sent[i] || !f) return acc;
    acc[sent[i].k] = { ...acc[sent[i].k], [f === "price_usd" ? "price" : f]: String(d.msg || "").replace(/^Value error, /, "") };
    return acc;
  }, {});
}
